import { ChainOfCustodyItem, SingleShipmentDataExtended } from '@/apis/types';
import { CoCEventType } from '@/common/enum';
import { StarIcon } from '@/assets/icons/icons';
import { Button, Flex, message, Tag } from 'antd';
import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { FileDownload } from '../../../assets/images';
import { CustomParagraph } from '../../../components';
import '../../../pages/Shipments/MapExternalView/Style.css';
import CommentAudienceIcons from './COC_Privacy_Comments/CommentAudienceIcons';
import OneTimeCOCAlert, { COCFormattedTime } from './OneTimeCOCAlert';
import { ExportReport } from '@/apis/gxpReport';
import { useSpin } from '@/components/SpinContext';
import useUnauthorize from '@/hooks/useUnauthorize';
import useToken from '@/common/helper/useToken';
import { getSystemFileUploadLabel } from './getSystemFileUploadLabel';

interface TimelineItemExternalProps {
  isSharePage?: boolean;
  custody: ChainOfCustodyItem;
  setShowFull: React.Dispatch<React.SetStateAction<boolean>>;
  showFull: boolean;
  getFormattedTime: (timestamp: string) => COCFormattedTime;
  downloadFile: (file: string, fileName: string) => void;
  setHalfHeight: (custodyId: string, height: number) => void;
  shipment?: Partial<SingleShipmentDataExtended>;
  getNotificationLabel: (method?: string) => string;
  fetchSingleShipmentData?: (showSpinner: boolean) => void;
}

/**
 * Component for displaying external timeline items in shipment custody chain
 *
 * @param custody - Custody record with action, comment, and file information
 * @param setShowFull - Function to toggle between full and relative time display
 * @param showFull - Boolean indicating whether to show full timestamp
 * @param getFormattedTime - Function to format timestamp into full and relative formats
 * @param downloadFile - Function to download attached files
 * @param setHalfHeight - Function to set the half height for timeline positioning
 * @param shipment - Shipment data containing custody chain
 */
const SENSOR_HANDLER_LABELS: Record<string, string> = {
  tive: 'Tive',
  onasset: 'OnAsset',
  roambee: 'Roambee',
  elpro: 'Elpro',
  tempmate: 'Tempmate',
  trackonomy: 'Trackonomy',
};

const TimelineItemExternal: React.FC<TimelineItemExternalProps> = ({
  custody,
  setShowFull,
  showFull,
  getFormattedTime,
  downloadFile,
  setHalfHeight,
  shipment,
  getNotificationLabel,
  fetchSingleShipmentData,
}) => {
  const { token } = useToken();
  const { unauthorize } = useUnauthorize();
  const { showSpin, hideSpin } = useSpin();
  const systemFileLabel =
    custody?.type === CoCEventType.FileUpload && custody?.created_by === 6
      ? getSystemFileUploadLabel(custody?.file_name)
      : null;

  const parentRef = useRef<HTMLDivElement>(null);
  const repliesRef = useRef<HTMLDivElement>(null);

  const setHalfHeightRef = useRef(setHalfHeight);
  useEffect(() => {
    setHalfHeightRef.current = setHalfHeight;
  }, [setHalfHeight]);

  useLayoutEffect(() => {
    const measureHeights = () => {
      if (!parentRef.current) return;

      const parentHeight = parentRef.current.getBoundingClientRect().height;
      const repliesHeight = repliesRef.current
        ? repliesRef.current.getBoundingClientRect().height
        : 0;

      const totalHeight = parentHeight + repliesHeight;
      setHalfHeightRef.current?.(custody.id, totalHeight / 2);
    };

    const rafId = requestAnimationFrame(measureHeights);
    return () => cancelAnimationFrame(rafId);
  }, [custody?.id, shipment?.custody]);

  const handleGxpDownload = useCallback(() => {
    if (!custody?.export_url || !token) return;
    showSpin();

    const queryParam = custody.export_url.replace(/^.*\/gxp\/reports\//, '');
    const exportFormat = queryParam.match(/export_format=(\w+)/)?.[1] ?? 'pdf';

    ExportReport(queryParam, token)
      .then((res) => {
        const blob: Blob = res?.data;
        if (!blob || blob.size === 0) return;

        const disposition: string = res?.headers?.['content-disposition'] ?? '';
        const filenameMatch = disposition.match(
          /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
        );
        const filename = filenameMatch
          ? filenameMatch[1].replace(/['"]/g, '').trim()
          : `report.${exportFormat}`;

        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = filename;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        URL.revokeObjectURL(url);

        message.success('Export report successfully.');
      })
      .catch((error) => {
        unauthorize(error);
      })
      .finally(() => {
        hideSpin();
      });
  }, [custody?.export_url, token, showSpin, hideSpin, unauthorize]);

  return (
    <div>
      <div
        ref={parentRef}
        className='flex-center btw y w-full relative gap-x-1'
      >
        <Flex align='center' gap={12}>
          {/* coc heading and AI icon */}
          <Flex gap={4} align='center'>
            {custody?.type === CoCEventType.OneTimeNotification ? (
              <>
                <p className={`zl-para-12`}>
                  {custody.is_from_automation
                    ? 'Automation'
                    : 'One-time message'}
                  : {getNotificationLabel(custody?.notification_method) || ''}
                </p>
                {custody?.notification_method === 'AI_CALL' ||
                custody?.notification_method === 'AI_EMAIL' ? (
                  <span className='flex-center AI-star-fill-color'>
                    <StarIcon width={16} height={16} />
                  </span>
                ) : null}
              </>
            ) : (
              <>
                <p
                  // inbound change here
                  className={`zl-para-12 flex-center gap-x-1 ${custody?.type === CoCEventType.FileUpload && 'word-break-all'}`}
                >
                  {systemFileLabel
                    ? systemFileLabel
                    : SENSOR_HANDLER_LABELS[custody?.handler ?? '']
                      ? `${SENSOR_HANDLER_LABELS[custody?.handler ?? '']} ${(custody?.action ?? '').replace(/^Sensor\s+/i, '')}`
                      : custody?.action}

                  {/* // inbound change here AI star with parent */}
                  {custody?.type === CoCEventType.AiCallAlert && (
                    <StarIcon width={16} height={16} />
                  )}
                </p>
              </>
            )}
          </Flex>
          {custody?.file && custody?.type === CoCEventType.FileUpload && (
            <Button
              type='default'
              className='zl-btn icon-btn-36 radius-full'
              onClick={(e) => {
                e.stopPropagation();
                downloadFile(custody.file!, custody.file_name!);
              }}
            >
              <img
                src={FileDownload}
                width={16}
                alt='Download file'
                className='zl-invert-on-dark'
              />
            </Button>
          )}
          {custody?.export_url && custody?.type === CoCEventType.GxPReport && (
            <Button
              type='default'
              className='gxp-report-download'
              onClick={(e) => {
                e.stopPropagation();
                handleGxpDownload();
              }}
            >
              <img
                src={FileDownload}
                width={16}
                alt='Download file'
                className='zl-invert-on-dark'
              />
            </Button>
          )}
        </Flex>
        <Flex align='center' gap={8}>
          {custody?.type === CoCEventType.Comment && (
            <CommentAudienceIcons
              audienceType={custody?.audience_type ?? ''}
              teams={custody?.audience_teams}
            />
          )}

          <span
            onClick={(e) => {
              setShowFull((prev) => !prev);
              e.stopPropagation();
            }}
            className='cursor-pointer'
          >
            <CustomParagraph
              text={
                showFull
                  ? getFormattedTime(custody?.timestamp)?.full
                  : getFormattedTime(custody?.timestamp)?.relative
              }
              fontSize='12px'
              fontWeight='400'
              lineHeight='16px'
              color='var(--text-light)'
              marginBottom='0'
              style={{ whiteSpace: 'nowrap' }}
            />
          </span>
        </Flex>
      </div>
      {/* inbound change here tag with parent */}
      {custody?.status_info?.description === 'Needs attention' &&
      custody?.type === CoCEventType.AiCallAlert ? (
        <Tag className=' mt-2 mb-2 one-time-status-tag need-attention'>
          Needs attention
        </Tag>
      ) : null}

      {custody?.type === CoCEventType.OneTimeNotification && (
        <OneTimeCOCAlert
          isControlTower={false}
          data={custody}
          getFormattedTime={getFormattedTime}
          setShowFull={setShowFull}
          showFull={showFull}
          shipmentId={shipment?.id ?? ''}
          // toggleDrawer={toggleDrawer}
          parentRef={parentRef}
          repliesRef={repliesRef}
          fetchSingleShipmentData={fetchSingleShipmentData}
        />
      )}

      <CustomParagraph
        text={String(custody?.comment || '')}
        fontSize='12px'
        fontWeight='400'
        lineHeight='16px'
        color='var(--text-light)'
        marginBottom='4px'
      />

      {/* Parent comment  */}
      {custody?.location_is_plain_text ? (
        <div ref={repliesRef} className='reply-comment-message'>
          {custody?.location ?? ''}
        </div>
      ) : (
        <div
          ref={repliesRef}
          className='reply-comment-message'
          dangerouslySetInnerHTML={{
            __html: custody?.location,
          }}
        />
      )}
    </div>
  );
};

export default TimelineItemExternal;
